import { useMemo } from 'react';
import { Flex } from 'antd';
import dayjs from 'dayjs';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './dashboard.module.scss';
import layout from '@/styles/layout.module.scss';
import Card from '@/components/card/card';
import KpiTile from '@/components/kpi-tile/kpi-tile';
import Meter from '@/components/meter/meter';
import Legend from '@/components/legend/legend';
import Callout from '@/components/callout/callout';
import HoursChart, { hoursChartColors } from '@/components/hours-chart/hours-chart';
import TopicsDonut from '@/components/topics-donut/topics-donut';
import TrendChart from '@/components/trend-chart/trend-chart';
import { CustomFilters } from '@/components/custom-filters/custom-filters';
import { CallLogsTable } from '@/components/call-logs-table/call-logs-table';
import { useScreenFilters } from '@/hooks/use-screen-filters/use-screen-filters';
import { useCallMetric } from '@/hooks/use-call-metric/use-call-metric';
import { useInsights } from '@/hooks/use-insights/use-insights';
import { useCallLog } from '@/hooks/use-call-log/use-call-log';
import { useBusinessHours } from '@/hooks/use-business-hours/use-business-hours';
import { DashboardFilters } from '@/services/dashboard-service/dashboard.service.types';
import { selectedRangeDays } from '@/services/dashboard-service/time-range.utils';
import {
  afterHoursShare,
  buildKpiTiles,
  hasHourlyVolume,
  hasOpenHours,
  hourOfDaySeries,
  officeWindowForRange,
  toTrendSeries,
  trendPointsAreHourly,
} from './dashboard.utils';
import { RECENT_CALL_ROWS, rangeKey } from './dashboard.constants';

export function Dashboard() {
  const { t } = useTranslation(['dashboard', 'metrics']);
  const {
    scopedFilter,
    selectedFilter,
    setPageFilter,
    canQuery,
    setCompanyName,
    defaultCompanyName,
    companies,
    hasMultipleCompanies,
    isFetched,
    isLoadingUserData,
    userData,
    setCompanySearchQuery,
    companySearchData,
    companySearchLoading,
    companySearchError,
  } = useScreenFilters();

  const {
    callMetricData,
    isLoading: isLoadingMetrics,
    error: metricsError,
  } = useCallMetric(scopedFilter, canQuery);
  const {
    insightsData,
    isLoading: isLoadingInsights,
    error: insightsError,
  } = useInsights(scopedFilter, canQuery);

  const { callLogData, isPending: isLoadingCallLog } = useCallLog({
    filtersParams: scopedFilter,
    paginationParams: {
      sort_by: 'created_at',
      order_by: 'desc',
      page: 1,
      items_per_page: RECENT_CALL_ROWS,
    },
  });

  // From the picker, falling back to the viewer's own company only while the
  // selection is still their default — an unresolvable selection must yield no id.
  const selectedCompanyId = companies?.find(
    (company) => company.name === selectedFilter.company_name
  )?.id;
  const ownCompanyIsSelected =
    !selectedFilter.company_name || selectedFilter.company_name === defaultCompanyName;
  const hoursCompanyId = (
    selectedCompanyId ?? (ownCompanyIsSelected ? userData?.company?.id : undefined)
  )?.toString();
  // The endpoint defaults an absent date to its own today, which is UTC, while the days
  // below are the viewer's own. Sending the local date keeps the two on the same day
  // either side of midnight, and the month of dated rows around the one the range needs.
  const { businessHoursData, isLoading: isLoadingHours } = useBusinessHours(
    hoursCompanyId,
    dayjs().format('YYYY-MM-DD')
  );

  // Not `inferActivePreset`: it snaps a custom span to any preset within a day of it,
  // which is right for highlighting a button and wrong for naming the span in copy,
  // where a 2-day range would title itself "last 24 hours". Copy names only a preset
  // the store actually holds.
  const activePreset = selectedFilter.time_range ?? 'custom';

  // A gated-off query reports neither loading nor error, so the ladders below read this.
  const isResolvingViewer = isLoadingUserData;

  // The donut renders nothing for an empty payload, so without a message of its own
  // a loading or empty window leaves a titled card with a blank body.
  const hasTopics = Boolean(Object.keys(insightsData?.trending_topics ?? {}).length);
  const topicsMessage = insightsError
    ? t('dashboard:topics.messages.error')
    : isResolvingViewer || isLoadingInsights
      ? t('dashboard:topics.messages.loading')
      : insightsData && !hasTopics
        ? t('dashboard:topics.messages.empty')
        : null;

  // Every day the bars count, not just today: the series is one hour-of-day distribution
  // over the whole range, so shading it by today's clock marked a week of weekday calls
  // after-hours whenever today happened to be closed.
  const rangeDays = useMemo(
    () => selectedRangeDays(selectedFilter),
    [selectedFilter.time_range, selectedFilter.start_time, selectedFilter.end_time]
  );
  // Memoised because it walks the whole payload once per day and does not depend on the
  // search text that re-renders this page on every keystroke.
  const office = useMemo(
    () => officeWindowForRange(businessHoursData, rangeDays),
    [businessHoursData, rangeDays]
  );
  // From the same leaf as the tile above, so the two figures cannot disagree.
  const afterHours = afterHoursShare(callMetricData);
  // Several companies land in one answer under a single clock, and which one depends on
  // the caller, so no office's window can be named beside the bars.
  const spansCompanies = hasMultipleCompanies && !selectedFilter.company_name;
  // An unknown window shades nothing, which is the honest rendering across several.
  const shadedWindow = spansCompanies ? undefined : office;
  // Stating hours are unavailable before the answer arrives is a false claim, not a
  // neutral placeholder.
  const officeHint = isLoadingHours
    ? t('dashboard:office.checking')
    : spansCompanies
      ? t('dashboard:office.allCompanies')
      : office.hint;
  const hourlySeries = hourOfDaySeries(callMetricData);
  // Gated on arrival like the trend card: a query the gate never sent reports neither
  // loading nor error, and an absent leaf and a quiet window are different answers.
  const hoursMessage = metricsError
    ? t('dashboard:hours.messages.error')
    : isResolvingViewer || isLoadingMetrics
      ? t('dashboard:hours.messages.loading')
      : !callMetricData
        ? null
        : !hourlySeries
          ? t('dashboard:hours.messages.unavailable')
          : hasHourlyVolume(hourlySeries)
            ? null
            : t('dashboard:hours.messages.empty');

  const tiles = useMemo(
    () =>
      buildKpiTiles({
        metrics: callMetricData,
        satisfaction: insightsData?.overall_satisfaction,
        preset: activePreset,
      }),
    [callMetricData, insightsData?.overall_satisfaction, activePreset]
  );

  const resolvedPercent = Math.round(callMetricData?.ai_resolution.current_percent ?? 0);
  const trendTitle = t('dashboard:trend.title', {
    range: t(`metrics:range.in.${rangeKey(activePreset)}`),
  });
  // Bucket size follows the calendar span, so a fixed "daily" hint would contradict
  // the axis whenever the range resolves to hourly buckets.
  const isHourly = trendPointsAreHourly(callMetricData?.chart ?? []);
  const trendHint = t(isHourly ? 'dashboard:trend.hint.hourly' : 'dashboard:trend.hint.daily');

  // The store owns the range, so the three fields are dropped rather than mirrored back.
  const handleCompanyChange = ({
    time_range: _range,
    start_time: _start,
    end_time: _end,
    ...filters
  }: DashboardFilters) => {
    setPageFilter(filters);
    setCompanyName(filters.company_name ?? defaultCompanyName);
  };

  const trendPoints = useMemo(
    () => toTrendSeries(callMetricData?.chart ?? []),
    [callMetricData?.chart]
  );
  // An all-zero series is full-length, so a length check would draw a flat baseline
  // under a title reporting volume. Agent-only tenants land here legitimately.
  const hasTrendVolume = trendPoints.some((point) => point.value > 0);

  return (
    <Flex vertical className={layout.page}>
      {isFetched && (
        <CustomFilters
          companiesList={companies?.map((company) => company.name) || []}
          filters={selectedFilter}
          defaultCompanyValue={defaultCompanyName}
          onChangeFilters={handleCompanyChange}
          onSearchQueryChange={setCompanySearchQuery}
          searchResults={companySearchData}
          isSearchLoading={companySearchLoading}
          searchError={companySearchError}
        />
      )}

      {/* A failed request otherwise renders as a measured zero across every tile. */}
      {metricsError && <Callout tone="strong">{t('dashboard:errors.metrics')}</Callout>}

      {/* Marked busy rather than left asserting zeros as measured fact. */}
      <div
        aria-busy={isResolvingViewer || isLoadingMetrics || undefined}
        className={clsx(layout.kpis, (isResolvingViewer || isLoadingMetrics) && layout.pending)}
      >
        {tiles.map((tile) => (
          <KpiTile
            key={tile.key}
            label={tile.label}
            value={tile.value}
            unit={tile.unit}
            note={tile.note}
            delta={tile.delta}
            variant={tile.variant}
            demoBadge={tile.demoBadge}
          />
        ))}
      </div>

      <div className={layout.gridWide}>
        <Card hint={officeHint} title={t('dashboard:hours.title')}>
          {hoursMessage ? (
            <p className={layout.empty}>{hoursMessage}</p>
          ) : (
            <>
              <HoursChart
                label={t('dashboard:hours.chartLabel')}
                closeHour={shadedWindow?.closeHour}
                openHour={shadedWindow?.openHour}
                values={hourlySeries ?? []}
              />
              {/* The key needs both tones on screen to explain. A closed day is shaded
                  entirely off-hours, so it gets the callout without the key. */}
              {shadedWindow && hasOpenHours(shadedWindow) && (
                <Legend
                  items={[
                    { label: t('dashboard:hours.legend.inHours'), color: hoursChartColors.inHours },
                    {
                      label: t('dashboard:hours.legend.outOfHours'),
                      color: hoursChartColors.offHours,
                    },
                  ]}
                />
              )}
            </>
          )}
          {/* Outside the branch above: this reads its own leaf, which arrives whether or
              not the series does. */}
          {afterHours !== null && (
            <Callout>{t('dashboard:hours.afterHoursNote', { percent: afterHours })}</Callout>
          )}
        </Card>

        <Card hint={t('dashboard:topics.hint')} title={t('dashboard:topics.title')}>
          {topicsMessage ? (
            <p className={layout.empty}>{topicsMessage}</p>
          ) : (
            <TopicsDonut topics={insightsData?.trending_topics ?? {}} />
          )}
        </Card>
      </div>

      <div className={layout.gridNarrow}>
        <Card hint={t('dashboard:effectiveness.hint')} title={t('dashboard:effectiveness.title')}>
          {metricsError ? (
            <p className={layout.empty}>{t('dashboard:effectiveness.error')}</p>
          ) : (
            // Marked busy on the same terms as the KPI row: an unanswered request
            // would otherwise read as a measured zero across the meters.
            <div
              aria-busy={isResolvingViewer || isLoadingMetrics || undefined}
              className={clsx((isResolvingViewer || isLoadingMetrics) && layout.pending)}
            >
              <div className={styles.effectiveness}>
                <b className={styles.effectivenessValue}>{`${resolvedPercent}%`}</b>
                <span className={styles.effectivenessCaption}>
                  {t('dashboard:effectiveness.caption')}
                </span>
              </div>
              <div className={styles.bars}>
                <Meter
                  count={String(callMetricData?.ai_resolution.value ?? 0)}
                  label={t('dashboard:effectiveness.resolved')}
                  labelWidth={170}
                  layout="row"
                  tone="success"
                  value={callMetricData?.ai_resolution.current_percent ?? 0}
                />
                <Meter
                  count={String(callMetricData?.agent_handoff.value ?? 0)}
                  label={t('dashboard:effectiveness.handedOff')}
                  labelWidth={170}
                  layout="row"
                  tone="series5"
                  value={callMetricData?.agent_handoff.current_percent ?? 0}
                />
              </div>
            </div>
          )}
        </Card>

        <Card hint={trendHint} title={trendTitle}>
          {/* Gated on the payload having arrived, not on a loading flag: a query the
              gate never sent reports neither loading nor error, and "no calls" would
              then be asserted about a period nobody asked about. */}
          {metricsError || (callMetricData && !hasTrendVolume) ? (
            <p className={layout.empty}>
              {metricsError
                ? t('dashboard:trend.messages.error')
                : t('dashboard:trend.messages.empty')}
            </p>
          ) : (
            <TrendChart label={trendTitle} points={trendPoints} />
          )}
        </Card>
      </div>

      <Card hint={t('dashboard:recent.hint')} title={t('dashboard:recent.title')}>
        <CallLogsTable
          callLogData={callLogData?.calls}
          loading={isLoadingCallLog}
          onChange={() => undefined}
          showCompany={hasMultipleCompanies}
          sortable={false}
        />
        <Link className={layout.cta} to="/conversations">
          {t('dashboard:recent.seeAll')}
        </Link>
      </Card>
    </Flex>
  );
}
